import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { SOCIAL } from "@/data/book";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill all the fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent. I'll reply soon.");
    } catch (err) {
      toast.error("Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="paper-grain">
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-16 items-start">
          <div>
            <p className="font-ui text-[11px] uppercase tracking-[0.35em] text-primary">Get in touch</p>
            <h1 className="font-display text-5xl md:text-6xl text-foreground mt-3 leading-[1.02] tracking-tight">
              Write to me.
            </h1>
            <p className="mt-4 font-display italic text-2xl text-secondary">Letters land in my inbox.</p>
            <div className="wobble-divider mt-6" />
            <p className="mt-6 font-body text-lg leading-relaxed text-foreground/85 max-w-md">
              Questions, reflections, corrections, or simply a story of how one of these truths has shown
              up in your week — I read each one. Replies come when there is something real to say.
            </p>
            <a
              href={`mailto:${SOCIAL.email}`}
              data-testid="contact-direct-email"
              className="mt-8 inline-flex items-center gap-2 font-ui text-sm text-primary link-soft"
            >
              <Mail className="h-4 w-4" /> {SOCIAL.email}
            </a>
          </div>

          <div className="bg-card border border-border rounded-2xl p-7 md:p-9 shadow-sm">
            {done ? (
              <div className="text-center py-8" data-testid="contact-success">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 text-primary mb-4">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h3 className="font-display text-3xl text-foreground">Received with thanks.</h3>
                <p className="mt-3 font-body text-foreground/80">
                  I&apos;ll write back as soon as I can — usually within a few days.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div>
                  <Label htmlFor="contact-name" className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">Your name</Label>
                  <Input id="contact-name" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 font-ui h-12 bg-background" />
                </div>
                <div>
                  <Label htmlFor="contact-email" className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
                  <Input id="contact-email" data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 font-ui h-12 bg-background" />
                </div>
                <div>
                  <Label htmlFor="contact-message" className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">Message</Label>
                  <Textarea id="contact-message" data-testid="contact-message" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 font-body bg-background" />
                </div>
                <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
                  {loading ? "Sending…" : <>Send <Send className="h-4 w-4 ml-2" /></>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
